// ═══════════════════════════════════════════════════════════════════════════════
//  src/state/onboardingStore.js
//  MICROSERVICIO ONBOARDING v3 — Estado del "Motor de Diagnóstico"
//
//  PUNTO DE DECISIÓN A (docs/onboarding-master-v2.md, Parte IV §1) — RESUELTO:
//  ────────────────────────────────────────────────────────────────────────────
//  Estrategia HÍBRIDA, confirmada con el dueño del producto:
//
//    · localStorage desde la Pantalla 1 → cubre el requisito no negociable del
//      documento ("sobrevivir a un refresh y estar disponible desde cualquier
//      ruta del sitio"), con latencia cero y sin pedir nada al usuario.
//    · UN SOLO sync al backend en la Pantalla 5.5, cuando el usuario ya invirtió
//      6+ preguntas y decide (o no) dejar su contacto → convierte el abandono en
//      remarketable, que es lo que localStorage por sí solo no puede hacer.
//    · Un segundo sync en la Pantalla 7 solo para marcar `completed_no_signup`.
//
//  Por qué NO se sincroniza en cada pantalla: el documento promete un flujo
//  "rápido y sin fricción"; escribir en el backend en cada tap multiplicaría
//  las escrituras sin ganar nada — antes de la 5.5 no hay contacto que asociar,
//  así que una fila creada en la Pantalla 1 sería un lead sin forma de contacto.
//
//  AISLAMIENTO (Protocolo Central NSL §4): store propio, sin dependencias de
//  ningún contexto o estado global del core. Ningún componente existente lo
//  importa; solo el módulo /diagnostico y el botón de retorno del header.
//
//  FECHAS: `last_updated_at` siempre UTC / ISO-8601 (Protocolo Central NSL §1).
// ═══════════════════════════════════════════════════════════════════════════════

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'nsl_onboarding_v3';
const LEAD_ENDPOINT = '/api/v3/onboarding/lead';

// La sesión del diagnóstico, para que el registro pueda recuperar lo que esta
// persona ya contestó en la puerta y no preguntárselo otra vez (22-09-2026).
export function sesionDelDiagnostico() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw).session_id || null) : null;
  } catch {
    return null;   // ventana privada o almacenamiento bloqueado
  }
}

/**
 * Versión del esquema del payload guardado en el navegador.
 *
 * ⚠️  SUBIR ESTE NÚMERO cada vez que cambie la FORMA del payload o el orden de
 *     las pantallas — no cuando solo cambie copy.
 *
 *     Una sesión guardada con un esquema viejo NO se puede reanudar de forma
 *     coherente: al usuario le faltarían respuestas que el flujo nuevo sí pide
 *     y llegaría al final con un diagnóstico incompleto. Es preferible que
 *     empiece de nuevo (el flujo dura minutos) a entregarle un plan armado con
 *     datos que nunca dio.
 *
 *   Historial:
 *     1 → v2  : deadline + time_commitment + learning_style
 *     2 → v2.2: training_intensity + feedback_style + campos *_custom,
 *               y Lobby de Bienvenida antepuesto al diagnóstico (paso 0).
 */
const SCHEMA_VERSION = 2;

/* ─────────────────────────────────────────────────────────────────────────────
   Payload inicial — espejo exacto de docs/onboarding-master-v2.md Parte I §8
   ───────────────────────────────────────────────────────────────────────────── */

function createInitialState() {
  return {
    // Sella el esquema con el que se guardó esta sesión.
    _schema: SCHEMA_VERSION,

    // ── Identidad de sesión anónima ──
    session_id: generateSessionId(),

    // ── Payload del documento maestro v2.2 (§8) ──
    aspiration: null,
    aspiration_custom: null,        // [v2.1] texto libre de "Otro objetivo..."
    trigger_event: null,
    trigger_event_custom: null,     // [v2.1] texto libre de "Otro motivo..."
    training_intensity: null,       // [v2.1] sustituye a `deadline`
    context_type: null,
    past_attempts: null,
    perceived_level: null,
    feedback_style: null,           // [v2.2] sustituye a time_commitment + learning_style
    technical_score: null,
    calibration_answers: [],
    route_assigned: null,
    contact_soft_capture: null,
    contact_email: null,
    contact_phone: null,
    funnel_stage_reached: null,   // in_progress | completed_no_signup
    last_updated_at: null,

    // ── Detalle de calibración (Parte II §1) ──
    calibration_detail: null,

    // ── Control de navegación interno (no forma parte del payload del doc) ──
    // 0 = Lobby de bienvenida. Toda sesión nueva arranca ahí; las que ya
    // estaban en curso conservan el paso que tuvieran guardado.
    _currentStep: 0,              // pantalla en la que se quedó, para reanudar
    _startedAt: nowUtc(),
  };
}

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback para navegadores sin crypto.randomUUID (respeta [A-Za-z0-9-]{8,64}).
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Protocolo Central NSL §1: todo timestamp sale en UTC / ISO-8601. */
function nowUtc() {
  return new Date().toISOString();
}

/* ─────────────────────────────────────────────────────────────────────────────
   Store minimalista con pub/sub — sin dependencias nuevas
   ───────────────────────────────────────────────────────────────────────────── */

let state = loadFromStorage();
const listeners = new Set();

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();

    const parsed = JSON.parse(raw);

    // Sesión de un esquema anterior → se descarta y se empieza limpio.
    // Reanudarla dejaría al usuario sin responder preguntas que el flujo actual
    // sí necesita (ver comentario de SCHEMA_VERSION).
    if (parsed?._schema !== SCHEMA_VERSION) return createInitialState();

    // Merge defensivo: si se añaden claves nuevas SIN cambiar el esquema, no
    // quedan undefined en las sesiones ya guardadas en el navegador.
    return { ...createInitialState(), ...parsed };
  } catch {
    return createInitialState();
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Modo incógnito / cuota llena: el flujo sigue funcionando en memoria.
    // Degradar en silencio es correcto aquí — perder el "guardado" no debe
    // romper el diagnóstico que el usuario está haciendo ahora mismo.
  }
}

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getState() {
  return state;
}

/**
 * Actualiza el payload. Toda escritura refresca `last_updated_at` en UTC
 * y marca el funnel como `in_progress` mientras no se haya completado.
 */
export function patch(partial) {
  state = {
    ...state,
    ...partial,
    last_updated_at: nowUtc(),
    funnel_stage_reached:
      partial.funnel_stage_reached ?? state.funnel_stage_reached ?? 'in_progress',
  };
  persist();
  emit();
  return state;
}

/** Guarda en qué pantalla se quedó, para poder reanudar exactamente ahí. */
export function setStep(step) {
  return patch({ _currentStep: step });
}

/** Borra la sesión de diagnóstico (usado al convertir a cuenta real). */
export function resetOnboarding() {
  state = createInitialState();
  persist();
  emit();
  return state;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Estado de retorno — Parte I §7.3 [NUEVO — v2]
   Dos estados diferenciados, no uno.
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * @returns {'in_progress'|'completed_no_signup'|null}
 *   null cuando no hay sesión guardada: el botón de retorno no debe mostrarse
 *   a un visitante que nunca empezó (regla explícita de Parte III §1).
 */
export function getResumeState() {
  const s = state;
  if (s.funnel_stage_reached === 'completed_no_signup') return 'completed_no_signup';

  // Hay progreso real solo si ya respondió al menos la Pantalla 1.
  const hasProgress = Boolean(s.aspiration);
  return hasProgress ? 'in_progress' : null;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Hooks de React
   ───────────────────────────────────────────────────────────────────────────── */

export function useOnboardingState() {
  return useSyncExternalStore(subscribe, getState, getState);
}

export function useResumeState() {
  return useSyncExternalStore(subscribe, getResumeState, getResumeState);
}

/* ─────────────────────────────────────────────────────────────────────────────
   Sync al microservicio backend — POST /api/v3/onboarding/lead
   ───────────────────────────────────────────────────────────────────────────── */

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Envía el payload al microservicio de onboarding.
 *
 * Es best-effort por diseño: si el backend falla, el usuario NO se entera y el
 * flujo continúa con el estado local intacto. Un lead perdido es un problema
 * de negocio; un flujo roto a mitad del diagnóstico es un problema peor.
 *
 * @param {Object} extra  campos adicionales a enviar en esta sincronización
 * @returns {Promise<boolean>} true si el backend confirmó
 */
export async function syncLead(extra = {}) {
  const s = { ...state, ...extra };

  const body = {
    session_id: s.session_id,
    contact_email: s.contact_email || null,
    contact_phone: s.contact_phone || null,
    contact_soft_capture: s.contact_soft_capture || null,
    aspiration: s.aspiration,
    aspiration_custom: s.aspiration_custom || null,
    trigger_event: s.trigger_event,
    trigger_event_custom: s.trigger_event_custom || null,
    training_intensity: s.training_intensity,
    context_type: s.context_type,
    past_attempts: s.past_attempts,
    perceived_level: s.perceived_level,
    feedback_style: s.feedback_style,
    technical_score: s.technical_score,
    estimated_cefr: s.calibration_detail?.estimated_cefr ?? null,
    route_assigned: s.route_assigned,
    funnel_stage_reached: s.funnel_stage_reached,
    last_updated_at: s.last_updated_at || nowUtc(),
    calibration_detail: s.calibration_detail
      ? {
          levels_shown: s.calibration_detail.levels_shown ?? [],
          per_level: s.calibration_detail.per_level ?? {},
          cutoff_triggered_at: s.calibration_detail.cutoff_triggered_at ?? null,
          estimated_cefr: s.calibration_detail.estimated_cefr ?? null,
          answers: s.calibration_detail.answers ?? [],
        }
      : null,
  };

  // El schema del backend usa extra="forbid": mandar claves en null es válido,
  // pero una clave inventada devolvería 422. Se limpian los undefined.
  Object.keys(body).forEach((k) => {
    if (body[k] === undefined) delete body[k];
  });

  try {
    const res = await fetch(`${API_BASE}${LEAD_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}
